/**
 * Time is server-authoritative. A student who changes their device clock must not unlock an
 * island early, so `day` always comes from the server and the client only formats it.
 *
 * The community is in Serbia; day boundaries are Europe/Belgrade, not UTC and not the browser's
 * guess. Otherwise someone posting at 01:00 CEST would see their day counter jump a day early.
 */

import { counted, WORDS } from './lang';

export const TZ = 'Europe/Belgrade';
export const DAY_MS = 86_400_000;

/** Calendar date in Belgrade, as YYYY-MM-DD. */
export function belgradeDate(at: number | Date): string {
  const date = typeof at === 'number' ? new Date(at) : at;
  // en-CA gives ISO-shaped output, which is the one thing that is stable across runtimes.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/** Whole days between two instants, counted by Belgrade calendar days. */
export function daysBetween(fromMs: number, toMs: number): number {
  const from = Date.parse(`${belgradeDate(fromMs)}T00:00:00Z`);
  const to = Date.parse(`${belgradeDate(toMs)}T00:00:00Z`);
  return Math.max(0, Math.round((to - from) / DAY_MS));
}

/**
 * Day of membership. Day 0 is the day the student joined Skool — not the day they first opened
 * this app. Someone six months into the academy must not be walled behind a day-7 lock.
 */
export function membershipDay(joinedAtMs: number, nowMs: number): number {
  return daysBetween(joinedAtMs, nowMs);
}

export function weekOfMembership(day: number): number {
  return Math.max(1, Math.floor(day / 7) + 1);
}

/** "pre 2 dana" / "danas" — for the journal and the feedback strip. */
export function relativeDays(fromMs: number, nowMs: number): string {
  const days = daysBetween(fromMs, nowMs);
  if (days === 0) return 'danas';
  if (days === 1) return 'juče';
  if (days < 7) return `pre ${days} dana`;
  const weeks = Math.floor(days / 7);
  if (weeks === 1) return 'pre nedelju dana';
  if (weeks < 5) return `pre ${weeks} nedelje`;
  const months = Math.floor(days / 30);
  // "pre mesec dana" is how people say it; past that, all three forms matter — 21 takes the
  // singular ("pre 21 mesec"), which is exactly what a hand-rolled two-way branch gets wrong.
  if (months === 1) return 'pre mesec dana';
  return `pre ${counted(months, WORDS.mesec)}`;
}

/**
 * "za 3 sata" / "sutra" / "za 5 dana" — how long until a deadline.
 *
 * `relativeDays` cannot do this and must not be asked to: it is a past-tense formatter and
 * `daysBetween` clamps at zero, so every future moment came out as „danas" — including the one
 * pasted into a Skool post telling the community when voting closes.
 */
export function untilWhen(atMs: number, nowMs: number): string {
  const ms = atMs - nowMs;
  if (ms <= 0) return 'rok je istekao';
  const hours = Math.floor(ms / 3_600_000);
  if (hours < 1) return 'za manje od sat vremena';
  if (hours < 24) return `za ${counted(hours, WORDS.sat)}`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'sutra';
  if (days < 7) return `za ${counted(days, WORDS.dan)}`;
  const weeks = Math.round(days / 7);
  return weeks === 1 ? 'za nedelju dana' : `za ${counted(weeks, WORDS.nedelja)}`;
}

/** Short absolute date for the journal: "14. mart". */
const MONTHS = [
  'januar',
  'februar',
  'mart',
  'april',
  'maj',
  'jun',
  'jul',
  'avgust',
  'septembar',
  'oktobar',
  'novembar',
  'decembar',
];

export function shortDate(at: number): string {
  const iso = belgradeDate(at);
  const [, month, day] = iso.split('-');
  const monthName = MONTHS[Number(month) - 1] ?? '';
  return `${Number(day)}. ${monthName}`;
}
